/**
 * Pulse aggregates for the super admin dashboard.
 * Live Stripe subscriptions are the only revenue. Active and past-due
 * subscriptions count as MRR. Trials count as customers at $0.
 * In live mode, plan-price estimates are not revenue and the sandbox
 * block is not built. Test-mode charges stay in a sandbox section.
 * AI rows are counts only. Message and response text is never copied.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

const AGENT_ROLES = ['agent', 'owner', 'member', 'team_member'];

const SOLO_CENTS = 4900;
const PLUS_CENTS = 1000;
const MAX_CENTS = 2500;
const HOMEOWNER_MONTH_CENTS = 499;
const HOMEOWNER_YEAR_CENTS = 4900;

const TEAM_CENTS = {
  2: 9800,
  3: 13300,
  4: 16800,
  5: 20300,
  6: 23800,
  7: 27300,
  8: 30800,
  9: 34300,
  10: 37800,
};

const AI_PER_HOMEOWNER = 20;

const FORBIDDEN_KEYS = new Set([
  'user_message',
  'ai_response',
  'agent_response',
  'thread_search',
  'appliance_info',
  'vendor_recommendations',
  'password',
  'password_hash',
  'prompt',
  'completion',
  'image_path',
  'image_name',
]);

const LINE_LABELS = {
  solo: 'Agent solo',
  team: 'Team seats',
  addon_plus: 'Homeowner Plus',
  addon_max: 'Homeowner Max',
  homeowner: 'Homeowner plans',
  other: 'Other',
};

function adminMfaRequired(env = process.env) {
  const source = env || {};
  const raw = String(source.ADMIN_REQUIRE_AAL2 || source.REACT_APP_ADMIN_REQUIRE_AAL2 || '').trim().toLowerCase();
  return raw === 'true' || raw === '1' || raw === 'on';
}

function adminPageRequiresAal2(env = process.env) {
  const raw = String((env && env.REACT_APP_ADMIN_REQUIRE_AAL2) || '').trim().toLowerCase();
  return raw === 'true' || raw === '1' || raw === 'on';
}

function stripeKeyKind(value) {
  const key = String(value || '').trim();
  if (key.indexOf('sk_live_') === 0 || key.indexOf('rk_live_') === 0) return 'live';
  if (key.indexOf('sk_test_') === 0 || key.indexOf('rk_test_') === 0) return 'test';
  return '';
}

function pulseStripeKey(env = process.env) {
  const source = env || {};
  const secret = String(source.STRIPE_SECRET_KEY || '').trim();
  if (stripeKeyKind(secret) === 'live') {
    return { key: secret, mode: 'live', from: 'STRIPE_SECRET_KEY' };
  }
  const names = ['STRIPE_PULSE_READ_KEY', 'STRIPE_RESTRICTED_KEY', 'STRIPE_SECRET_KEY'];
  for (let i = 0; i < names.length; i += 1) {
    const value = String(source[names[i]] || '').trim();
    if (stripeKeyKind(value) === 'test') return { key: value, mode: 'test', from: names[i] };
  }
  for (let i = 0; i < names.length; i += 1) {
    const value = String(source[names[i]] || '').trim();
    if (stripeKeyKind(value) === 'live') return { key: value, mode: 'live', from: names[i] };
  }
  return { key: null, mode: 'off', from: null };
}

function tokenAal(token) {
  const parts = String(token || '').split('.');
  if (parts.length < 2 || typeof atob !== 'function') return '';
  try {
    const pad = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = pad + '='.repeat((4 - (pad.length % 4)) % 4);
    const json = JSON.parse(atob(padded));
    return json && json.aal ? String(json.aal) : '';
  } catch (error) {
    return '';
  }
}

function isPlatformAgent(profile) {
  if (!profile || profile.role === 'super_admin' || profile.role === 'homeowner') return false;
  return AGENT_ROLES.indexOf(profile.role) !== -1;
}

function isTeamOwner(profile) {
  return Boolean(profile) && (profile.is_team_owner === true || profile.role === 'owner');
}

function isBillingProfile(profile) {
  if (!isPlatformAgent(profile)) return false;
  if (profile.role === 'member' || profile.role === 'team_member') return false;
  if (profile.team_id && !isTeamOwner(profile) && profile.subscription_plan === 'team') return false;
  return true;
}

function stamp(value) {
  const at = new Date(value).getTime();
  return Number.isFinite(at) ? at : null;
}

function inWindow(value, start, end) {
  const at = stamp(value);
  if (at == null) return false;
  if (start != null && at < start) return false;
  if (end != null && at >= end) return false;
  return true;
}

function personName(row, fallback) {
  if (!row) return fallback;
  const named = [row.first_name, row.last_name].filter(Boolean).join(' ').trim();
  if (named) return named.slice(0, 80);
  if (row.name) return String(row.name).slice(0, 80);
  return fallback;
}

function clip(value, max) {
  if (value == null) return '';
  return String(value).replace(/\s+/g, ' ').trim().slice(0, max || 140);
}

function emptyLine(id) {
  return {
    id,
    label: LINE_LABELS[id],
    cents: 0,
    customers: 0,
    seats: 0,
  };
}

function applyPercent(cents, percent) {
  const pct = Number(percent);
  if (!Number.isFinite(pct) || pct <= 0) return cents;
  const kept = Math.max(0, 100 - Math.min(pct, 100));
  return Math.round(cents * kept / 100);
}

function discountActive(profile, now) {
  const pct = Number(profile && profile.discount_percent);
  if (!Number.isFinite(pct) || pct <= 0) return 0;
  if (profile.discount_ends_at) {
    const end = stamp(profile.discount_ends_at);
    if (end != null && end < now.getTime()) return 0;
  }
  return Math.min(pct, 100);
}

function teamCents(seats) {
  const count = Number(seats);
  if (!Number.isInteger(count) || !TEAM_CENTS[count]) return 0;
  return TEAM_CENTS[count];
}

function addonCents(addon, seats) {
  const count = Math.max(1, Math.floor(Number(seats) || 1));
  if (addon === 'plus') return PLUS_CENTS * count;
  if (addon === 'max') return MAX_CENTS * count;
  return 0;
}

function intervalMonths(price) {
  const recurring = price && price.recurring;
  const interval = recurring && recurring.interval;
  const count = Math.max(1, Number(recurring && recurring.interval_count) || 1);
  if (interval === 'year') return 12 * count;
  if (interval === 'month') return count;
  if (interval === 'week') return (count * 7) / 30;
  return 1;
}

function classifyPrice(price, book) {
  const id = price && (typeof price === 'string' ? price : price.id);
  const meta = price && price.metadata ? price.metadata : {};
  const addon = String(meta.addon || '').toLowerCase();
  if (addon === 'plus' || (book.plus && id === book.plus)) return 'addon_plus';
  if (addon === 'max' || (book.max && id === book.max)) return 'addon_max';
  if (book.homeownerMonth && id === book.homeownerMonth) return 'homeowner';
  if (book.homeownerYear && id === book.homeownerYear) return 'homeowner';
  if (book.solo && id === book.solo) return 'solo';
  if (book.teamSeats && book.teamSeats[id]) return 'team';
  const plan = String(meta.plan_type || meta.plan || '').toLowerCase();
  if (plan === 'solo' || plan === 'team' || plan === 'homeowner') return plan;
  return 'other';
}

function subscriptionItems(subscription) {
  const data = subscription && subscription.items && subscription.items.data;
  return Array.isArray(data) ? data : [];
}

function lineCents(item) {
  const price = item && item.price;
  const unit = Number(price && price.unit_amount);
  if (!Number.isFinite(unit)) return 0;
  const quantity = Math.max(1, Number(item.quantity) || 1);
  const months = intervalMonths(price);
  if (!months) return 0;
  return Math.round((unit * quantity) / months);
}

function couponPercent(subscription) {
  const discount = subscription && (subscription.discount || (Array.isArray(subscription.discounts) && subscription.discounts[0]));
  const coupon = discount && discount.coupon;
  const pct = Number(coupon && coupon.percent_off);
  return Number.isFinite(pct) ? pct : 0;
}

function couponAmountOff(subscription) {
  const discount = subscription && (subscription.discount || (Array.isArray(subscription.discounts) && subscription.discounts[0]));
  const coupon = discount && discount.coupon;
  const amount = Number(coupon && coupon.amount_off);
  return Number.isFinite(amount) ? amount : 0;
}

function countedSubscription(subscription) {
  const status = subscription && subscription.status;
  return status === 'active' || status === 'trialing' || status === 'past_due';
}

function mrrFromStripe(subscriptions, book) {
  const lines = {
    solo: emptyLine('solo'),
    team: emptyLine('team'),
    addon_plus: emptyLine('addon_plus'),
    addon_max: emptyLine('addon_max'),
    homeowner: emptyLine('homeowner'),
    other: emptyLine('other'),
  };
  let payingAgents = 0;
  let teamCustomers = 0;
  let cancelling = 0;
  let trialing = 0;
  (subscriptions || []).forEach((subscription) => {
    if (!countedSubscription(subscription)) return;
    const onTrial = subscription.status === 'trialing';
    if (onTrial) trialing += 1;
    if (subscription.cancel_at_period_end) cancelling += 1;
    const parts = [];
    let seats = 0;
    let agentPlan = false;
    let teamPlan = false;
    subscriptionItems(subscription).forEach((item) => {
      const kind = classifyPrice(item && item.price, book || {});
      let cents = onTrial ? 0 : lineCents(item);
      if (!onTrial && !cents && kind === 'team') {
        const known = book.teamSeats && book.teamSeats[item.price && item.price.id];
        cents = teamCents(known);
      }
      if (!onTrial && !cents && kind === 'solo') cents = SOLO_CENTS;
      if (kind === 'team') {
        teamPlan = true;
        const known = book.teamSeats && book.teamSeats[item.price && item.price.id];
        seats = Math.max(seats, known || Number(item.quantity) || 1);
      }
      if (kind === 'addon_plus' || kind === 'addon_max') {
        seats = Math.max(seats, Number(item.quantity) || 1);
      }
      if (kind === 'solo') {
        agentPlan = true;
        seats = Math.max(seats, 1);
      }
      parts.push({ kind, cents });
    });
    if (!onTrial) {
      const percent = couponPercent(subscription);
      let total = 0;
      parts.forEach((part) => {
        part.cents = applyPercent(part.cents, percent);
        total += part.cents;
      });
      const off = couponAmountOff(subscription);
      if (off > 0 && total > 0) {
        const cut = Math.min(off, total);
        let left = cut;
        parts.sort((a, b) => b.cents - a.cents);
        parts.forEach((part) => {
          const take = Math.min(part.cents, left);
          part.cents -= take;
          left -= take;
        });
      }
    }
    parts.forEach((part) => {
      const line = lines[part.kind] || lines.other;
      line.cents += part.cents;
    });
    const hasAgent = parts.some((part) => part.kind === 'solo' || part.kind === 'team');
    const hasHomeowner = parts.some((part) => part.kind === 'homeowner');
    if (hasAgent && !onTrial) {
      payingAgents += 1;
      if (teamPlan) {
        teamCustomers += 1;
        lines.team.customers += 1;
        lines.team.seats += seats;
      } else if (agentPlan || parts.some((part) => part.kind === 'solo')) {
        lines.solo.customers += 1;
        lines.solo.seats += 1;
      }
    } else if (hasAgent && onTrial) {
      if (teamPlan) lines.team.customers += 1;
      else lines.solo.customers += 1;
    }
    if (!onTrial && parts.some((part) => part.kind === 'addon_plus')) {
      lines.addon_plus.customers += 1;
      lines.addon_plus.seats += seats || 1;
    }
    if (!onTrial && parts.some((part) => part.kind === 'addon_max')) {
      lines.addon_max.customers += 1;
      lines.addon_max.seats += seats || 1;
    }
    if (hasHomeowner) {
      lines.homeowner.customers += 1;
    }
  });
  return { lines, payingAgents, teamCustomers, cancelling, trialing };
}

function mrrFromPlans(profiles, teams, accounts, now) {
  const lines = {
    solo: emptyLine('solo'),
    team: emptyLine('team'),
    addon_plus: emptyLine('addon_plus'),
    addon_max: emptyLine('addon_max'),
    homeowner: emptyLine('homeowner'),
    other: emptyLine('other'),
  };
  const teamById = new Map((teams || []).map((team) => [team.id, team]));
  const seenTeams = new Set();
  (profiles || []).forEach((profile) => {
    if (!profile || profile.subscription_status !== 'active' || !isBillingProfile(profile)) return;
    const percent = discountActive(profile, now);
    if (profile.team_id && (profile.subscription_plan === 'team' || profile.subscription_plan === 'solo')) {
      if (!isTeamOwner(profile) || seenTeams.has(profile.team_id)) return;
      seenTeams.add(profile.team_id);
    }
    if (profile.subscription_plan === 'team' || (profile.team_id && profile.subscription_plan === 'team')) {
      const team = teamById.get(profile.team_id);
      const seats = team ? Number(team.max_seats) : 0;
      const base = applyPercent(teamCents(seats), percent);
      if (!base) return;
      lines.team.cents += base;
      lines.team.customers += 1;
      lines.team.seats += seats;
      const addon = (team && team.homeowner_addon) || profile.homeowner_addon;
      const extra = applyPercent(addonCents(addon, seats), percent);
      if (addon === 'plus') {
        lines.addon_plus.cents += extra;
        lines.addon_plus.customers += 1;
        lines.addon_plus.seats += seats;
      } else if (addon === 'max') {
        lines.addon_max.cents += extra;
        lines.addon_max.customers += 1;
        lines.addon_max.seats += seats;
      }
      return;
    }
    if (profile.subscription_plan === 'solo') {
      const base = applyPercent(SOLO_CENTS, percent);
      lines.solo.cents += base;
      lines.solo.customers += 1;
      lines.solo.seats += 1;
      const addon = profile.homeowner_addon;
      const extra = applyPercent(addonCents(addon, 1), percent);
      if (addon === 'plus') {
        lines.addon_plus.cents += extra;
        lines.addon_plus.customers += 1;
        lines.addon_plus.seats += 1;
      } else if (addon === 'max') {
        lines.addon_max.cents += extra;
        lines.addon_max.customers += 1;
        lines.addon_max.seats += 1;
      }
    }
  });
  (accounts || []).forEach((account) => {
    if (!account || account.plan_status !== 'active') return;
    let cents = 0;
    if (account.plan_interval === 'year') cents = Math.round(HOMEOWNER_YEAR_CENTS / 12);
    else if (account.plan_interval === 'month') cents = HOMEOWNER_MONTH_CENTS;
    if (!cents) return;
    lines.homeowner.cents += cents;
    lines.homeowner.customers += 1;
  });
  const payingAgents = lines.solo.customers + lines.team.customers;
  return { lines, payingAgents, teamCustomers: lines.team.customers, cancelling: 0 };
}

function sumLines(lines) {
  return Object.keys(lines).reduce((sum, key) => sum + (lines[key].cents || 0), 0);
}

function lineList(lines) {
  return ['solo', 'team', 'addon_plus', 'addon_max', 'homeowner', 'other']
    .map((id) => lines[id])
    .filter((line) => line && (line.cents > 0 || line.id !== 'other'));
}

function isArchivedClient(client) {
  if (!client) return false;
  if (client.archived_at) return true;
  return client.status === 'archived';
}

function countHomeowners(clients, now) {
  const since = now.getTime() - (30 * DAY_MS);
  const counts = { active30d: 0, invitedNotJoined: 0, archived: 0 };
  (clients || []).forEach((client) => {
    if (isArchivedClient(client)) {
      counts.archived += 1;
      return;
    }
    const active = stamp(client.last_active);
    if (active != null && active >= since) {
      counts.active30d += 1;
      return;
    }
    const joined = client.has_password === true
      || client.account_created === true
      || client.status === 'active'
      || client.invite_status === 'accepted';
    if (joined) return;
    if (
      client.status === 'invited'
      || client.invite_status === 'sent'
      || client.invite_status === 'not_sent'
      || client.invite_status === 'failed'
    ) {
      counts.invitedNotJoined += 1;
    }
  });
  return counts;
}

function signupCounts(profiles, accounts, now) {
  const day = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const week = now.getTime() - (7 * DAY_MS);
  const month = now.getTime() - (30 * DAY_MS);
  const windows = {
    today: { start: day, agents: 0, homeowners: 0 },
    d7: { start: week, agents: 0, homeowners: 0 },
    d30: { start: month, agents: 0, homeowners: 0 },
  };
  (profiles || []).forEach((profile) => {
    if (!isPlatformAgent(profile)) return;
    Object.keys(windows).forEach((key) => {
      if (inWindow(profile.created_at, windows[key].start, null)) windows[key].agents += 1;
    });
  });
  (accounts || []).forEach((account) => {
    Object.keys(windows).forEach((key) => {
      if (inWindow(account.created_at, windows[key].start, null)) windows[key].homeowners += 1;
    });
  });
  const pack = (key) => ({ agents: windows[key].agents, homeowners: windows[key].homeowners });
  return { today: pack('today'), d7: pack('d7'), d30: pack('d30') };
}

function mondayUtc(now) {
  const day = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const weekday = day.getUTCDay();
  const shift = weekday === 0 ? 6 : weekday - 1;
  day.setUTCDate(day.getUTCDate() - shift);
  return day;
}

function monthKey(date) {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(key) {
  const parts = String(key).split('-');
  const date = new Date(Date.UTC(Number(parts[0]), Number(parts[1]) - 1, 1));
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function weekLabel(key) {
  const date = new Date(`${key}T00:00:00.000Z`);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function mrrTrend(metricsDaily, currentCents, now) {
  const months = [];
  for (let i = 11; i >= 0; i -= 1) {
    const date = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    months.push(monthKey(date));
  }
  const latest = {};
  (metricsDaily || []).forEach((row) => {
    if (!row || !row.day) return;
    const key = String(row.day).slice(0, 7);
    if (!latest[key] || String(row.day) > String(latest[key].day)) latest[key] = row;
  });
  const current = monthKey(now);
  return months.map((key) => ({
    month: key,
    label: monthLabel(key),
    cents: key === current ? currentCents : Number((latest[key] && latest[key].mrr_cents) || 0),
  }));
}

function weeklySignups(profiles, accounts, now) {
  const start = mondayUtc(now);
  const weeks = [];
  for (let i = 11; i >= 0; i -= 1) {
    const date = new Date(start.getTime() - (i * 7 * DAY_MS));
    weeks.push(date.toISOString().slice(0, 10));
  }
  const buckets = {};
  weeks.forEach((week) => {
    buckets[week] = { week, label: weekLabel(week), agents: 0, homeowners: 0 };
  });
  const place = (value, field) => {
    const at = stamp(value);
    if (at == null) return;
    const key = mondayUtc(new Date(at)).toISOString().slice(0, 10);
    if (buckets[key]) buckets[key][field] += 1;
  };
  (profiles || []).forEach((profile) => {
    if (isPlatformAgent(profile)) place(profile.created_at, 'agents');
  });
  (accounts || []).forEach((account) => place(account.created_at, 'homeowners'));
  return weeks.map((week) => buckets[week]);
}

function trialStats(profiles, now) {
  const start = now.getTime() - (30 * DAY_MS);
  let active = 0;
  let ended = 0;
  let converted = 0;
  (profiles || []).forEach((profile) => {
    if (!isBillingProfile(profile)) return;
    const trialEnd = stamp(profile.trial_ends_at);
    if (profile.subscription_status === 'trialing' && (trialEnd == null || trialEnd >= now.getTime())) {
      active += 1;
    }
    if (trialEnd != null && trialEnd >= start && trialEnd < now.getTime()) {
      ended += 1;
      if (profile.subscription_status === 'active') converted += 1;
    }
  });
  return {
    active,
    ended30d: ended,
    converted30d: converted,
    trialToPaidPct: ended ? (converted / ended) * 100 : null,
  };
}

function churnStats(subscriptions, accounts, billingEvents, payingAgents, now, stripeCancelling) {
  const start = now.getTime() - (30 * DAY_MS);
  const deletedEvents = (billingEvents || []).filter((row) => (
    row && row.event_type === 'customer.subscription.deleted' && inWindow(row.occurred_at || row.created_at, start, null)
  )).length;
  const deletedRows = (subscriptions || []).filter((row) => (
    row && inWindow(row.canceled_at, start, null)
  )).length;
  const canceled30d = deletedEvents > 0 ? deletedEvents : deletedRows;
  let cancelling = Number.isInteger(stripeCancelling) ? stripeCancelling : 0;
  if (!Number.isInteger(stripeCancelling)) {
    (subscriptions || []).forEach((row) => {
      if (row && row.cancel_at_period_end && row.status !== 'canceled') cancelling += 1;
    });
    (accounts || []).forEach((account) => {
      if (account && account.plan_status === 'active' && account.cancel_at_period_end) cancelling += 1;
    });
  }
  const base = payingAgents + canceled30d;
  return {
    canceled30d,
    cancellingAtPeriodEnd: cancelling,
    ratePct: base ? (canceled30d / base) * 100 : null,
  };
}

function safeDetails(details) {
  if (!details || typeof details !== 'object' || Array.isArray(details)) return {};
  const keep = {};
  ['name', 'first_name', 'last_name', 'photoType', 'field', 'subscription_plan', 'subscription_status'].forEach((key) => {
    const value = details[key];
    if (value == null || typeof value === 'object') return;
    keep[key] = clip(value, 80);
  });
  return keep;
}

function activityFromAdmin(log) {
  const details = safeDetails(log.details);
  const name = personName(log.admin, 'An admin');
  const action = clip(log.action_type, 60) || 'action';
  const extra = [details.name, details.subscription_plan, details.subscription_status].filter(Boolean).join(', ');
  return {
    id: `admin:${log.id}`,
    at: log.created_at,
    kind: 'admin',
    title: `${name} recorded ${action.replace(/_/g, ' ')}`,
    detail: extra,
    targetType: '',
    targetId: '',
  };
}

function buildActivity(input) {
  const source = input || {};
  const testMode = source.stripeMode !== 'live';
  const rows = [];
  const profiles = source.profiles || [];
  const profileName = new Map(profiles.map((profile) => [profile.id, personName(profile, 'Agent')]));
  (source.profiles || []).forEach((profile) => {
    if (!isPlatformAgent(profile) || !profile.created_at) return;
    rows.push({
      id: `signup-agent:${profile.id}`,
      at: profile.created_at,
      kind: 'signup',
      title: 'New agent',
      detail: personName(profile, 'Agent'),
      targetType: 'agent',
      targetId: profile.id,
    });
  });
  (source.accounts || []).forEach((account) => {
    if (!account.created_at) return;
    rows.push({
      id: `signup-homeowner:${account.id}`,
      at: account.created_at,
      kind: 'signup',
      title: 'New homeowner',
      detail: personName(account, 'Homeowner'),
      targetType: 'client',
      targetId: account.client_id || '',
    });
  });
  (source.billingEvents || []).forEach((event) => {
    const type = event.event_type || '';
    let title = 'Billing event';
    let kind = 'payment';
    if (type === 'invoice.payment_succeeded') title = 'Payment received';
    else if (type === 'invoice.payment_failed') {
      title = 'Payment failed';
      kind = 'payment';
    } else if (type === 'customer.subscription.deleted') {
      title = 'Subscription canceled';
      kind = 'cancel';
    } else if (type === 'customer.subscription.created') {
      title = 'Subscription started';
      kind = 'upgrade';
    } else if (type === 'customer.subscription.updated') {
      title = 'Subscription updated';
      kind = 'upgrade';
    }
    const amount = Number(event.amount_cents);
    const rowTest = event.livemode === true ? false : (event.livemode === false ? true : testMode);
    rows.push({
      id: `bill:${event.id || event.stripe_event_id}`,
      at: event.occurred_at || event.created_at,
      kind,
      title,
      test: rowTest,
      detail: Number.isFinite(amount) ? money(amount) : clip(event.line, 40),
      targetType: event.profile_id ? 'agent' : '',
      targetId: event.profile_id || '',
    });
  });
  (source.teamInvites || []).forEach((invite) => {
    rows.push({
      id: `team-invite:${invite.id}`,
      at: invite.created_at,
      kind: 'invite',
      title: 'Team invite',
      detail: personName(invite, clip(invite.email, 80) || 'Invite'),
      targetType: '',
      targetId: '',
    });
  });
  (source.clientInvites || []).forEach((invite) => {
    rows.push({
      id: `client-invite:${invite.id}`,
      at: invite.created_at,
      kind: 'invite',
      title: 'Homeowner invite',
      detail: invite.status ? clip(invite.status, 40) : 'Sent',
      targetType: invite.client_id ? 'client' : '',
      targetId: invite.client_id || '',
    });
  });
  (source.sponsorInvites || []).forEach((invite) => {
    rows.push({
      id: `sponsor-invite:${invite.id}`,
      at: invite.created_at,
      kind: 'invite',
      title: 'Sponsor invite',
      detail: invite.accepted_at ? 'Accepted' : 'Waiting',
      targetType: '',
      targetId: '',
    });
  });
  (source.projects || []).forEach((project) => {
    rows.push({
      id: `project:${project.id}`,
      at: project.created_at,
      kind: 'project',
      title: 'Project added',
      detail: clip(project.title, 80),
      targetType: project.client_id ? 'client' : '',
      targetId: project.client_id || '',
    });
  });
  (source.reports || []).forEach((report) => {
    rows.push({
      id: `report:${report.id}`,
      at: report.created_at,
      kind: 'report',
      title: 'Home report',
      detail: clip(report.report_number, 40),
      targetType: report.client_id ? 'client' : '',
      targetId: report.client_id || '',
    });
  });
  (source.transfers || []).forEach((transfer) => {
    rows.push({
      id: `transfer:${transfer.id}`,
      at: transfer.claimed_at || transfer.created_at,
      kind: 'transfer',
      title: 'Home transfer',
      detail: clip(transfer.status, 40),
      targetType: transfer.buyer_client_id || transfer.seller_client_id ? 'client' : '',
      targetId: transfer.buyer_client_id || transfer.seller_client_id || '',
    });
  });
  (source.adminLogs || []).forEach((log) => {
    if (log && log.created_at) rows.push(activityFromAdmin(log));
  });
  (source.payments || []).forEach((payment) => {
    const failed = payment && payment.status === 'failed';
    if (!failed && (source.billingEvents || []).length) return;
    const rowTest = payment && payment.livemode === true ? false : (payment && payment.livemode === false ? true : testMode);
    rows.push({
      id: `payment:${payment.id}`,
      at: payment.created_at,
      kind: 'payment',
      title: failed ? 'Payment failed' : 'Payment received',
      test: rowTest,
      detail: money(payment.amount),
      targetType: 'agent',
      targetId: payment.agent_id || '',
    });
  });
  return rows
    .filter((row) => row && row.at)
    .sort((a, b) => stamp(b.at) - stamp(a.at))
    .slice(0, 120)
    .map((row) => {
      const detail = row.detail || profileName.get(row.targetId) || '';
      const tested = row.test && detail.indexOf('Test') === -1
        ? `${detail ? `${detail} · ` : ''}Test`
        : detail;
      return { ...row, detail: tested };
    });
}

function money(cents) {
  const value = Math.round(Number(cents) || 0);
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  const dollars = Math.floor(abs / 100);
  const rem = String(abs % 100).padStart(2, '0');
  return `${sign}$${dollars.toLocaleString('en-US')}.${rem}`;
}

function recentSignups(profiles, accounts, clients) {
  const clientByEmail = new Map();
  (clients || []).forEach((client) => {
    const email = String(client.email || '').trim().toLowerCase();
    if (email && !clientByEmail.has(email)) clientByEmail.set(email, client);
  });
  const rows = [];
  (profiles || []).forEach((profile) => {
    if (!isPlatformAgent(profile)) return;
    rows.push({
      id: profile.id,
      at: profile.created_at,
      kind: 'agent',
      name: personName(profile, 'New agent'),
      email: clip(profile.email, 120),
      targetType: 'agent',
      targetId: profile.id,
    });
  });
  (accounts || []).forEach((account) => {
    const email = clip(account.email, 120);
    const client = clientByEmail.get(email.toLowerCase());
    rows.push({
      id: account.id,
      at: account.created_at,
      kind: 'homeowner',
      name: personName(account, 'New homeowner'),
      email,
      targetType: 'client',
      targetId: (client && client.id) || account.client_id || '',
    });
  });
  return rows.filter((row) => row.at).sort((a, b) => stamp(b.at) - stamp(a.at)).slice(0, 12);
}

function trialsEnding(profiles, now) {
  const end = now.getTime() + (7 * DAY_MS);
  return (profiles || []).filter((profile) => {
    if (!isBillingProfile(profile) || profile.subscription_status !== 'trialing') return false;
    const at = stamp(profile.trial_ends_at);
    return at != null && at >= now.getTime() && at <= end;
  }).map((profile) => ({
    id: profile.id,
    name: personName(profile, 'Agent'),
    email: clip(profile.email, 120),
    at: profile.trial_ends_at,
    plan: clip(profile.subscription_plan, 20) || 'plan',
    targetType: 'agent',
    targetId: profile.id,
  })).sort((a, b) => stamp(a.at) - stamp(b.at)).slice(0, 12);
}

function failedPayments(payments, profiles, testMode) {
  const names = new Map((profiles || []).map((profile) => [profile.id, profile]));
  return (payments || []).filter((payment) => payment && payment.status === 'failed').map((payment) => {
    const profile = names.get(payment.agent_id);
    const test = payment.livemode === true ? false : (payment.livemode === false ? true : testMode === true);
    return {
      id: payment.id,
      name: personName(profile, 'Agent'),
      email: clip(profile && profile.email, 120),
      amountCents: Number(payment.amount) || 0,
      at: payment.created_at,
      test,
      targetType: profile ? 'agent' : '',
      targetId: profile ? profile.id : '',
    };
  }).sort((a, b) => stamp(b.at) - stamp(a.at)).slice(0, 12);
}

function inquiryRows(inquiries) {
  return (inquiries || []).map((row) => ({
    id: row.id,
    name: personName(row, 'Inquiry'),
    company: clip(row.company, 80),
    email: clip(row.email, 120),
    agentCount: Number(row.agent_count) || 0,
    at: row.created_at,
    status: clip(row.status, 40),
    targetType: 'inquiry',
    targetId: row.id,
  })).filter((row) => row.at).sort((a, b) => stamp(b.at) - stamp(a.at)).slice(0, 12);
}

function errorRows(clientErrors, jobs) {
  const rows = [];
  (clientErrors || []).forEach((row) => {
    rows.push({
      id: `client:${row.id}`,
      at: row.created_at || row.reported_at,
      source: 'client',
      title: clip(row.message, 180) || 'App error',
      detail: clip(row.url_path, 120),
    });
  });
  (jobs || []).forEach((row) => {
    if (!row || row.status !== 'failed') return;
    rows.push({
      id: `job:${row.id}`,
      at: row.finished_at || row.started_at,
      source: 'job',
      title: clip(row.job_name, 80) || 'Job failed',
      detail: clip(row.error, 180),
    });
  });
  return rows.filter((row) => row.at).sort((a, b) => stamp(b.at) - stamp(a.at)).slice(0, 20);
}

function aiUsage(count, clients, limit) {
  const capEach = Number(limit) > 0 ? Number(limit) : AI_PER_HOMEOWNER;
  const homes = (clients || []).filter((client) => !isArchivedClient(client)).length;
  const questions = Math.max(0, Number(count) || 0);
  return {
    questions,
    homeowners: homes,
    perHomeowner: capEach,
    cap: homes * capEach,
  };
}

function agentRoster(profiles) {
  const roster = { total: 0, owners: 0, members: 0, solo: 0 };
  (profiles || []).forEach((profile) => {
    if (!isPlatformAgent(profile)) return;
    roster.total += 1;
    if (isTeamOwner(profile)) roster.owners += 1;
    else if (profile.role === 'member' || profile.role === 'team_member') roster.members += 1;
    else if (!profile.team_id) roster.solo += 1;
  });
  return roster;
}

function teamSeatTotals(teams) {
  let used = 0;
  let total = 0;
  (teams || []).forEach((team) => {
    used += Math.max(0, Number(team.current_seats) || 0);
    total += Math.max(0, Number(team.max_seats) || 0);
  });
  return { count: (teams || []).length, seatsUsed: used, seatsTotal: total };
}

function stripeNote(mode, source, stripeError, hasLiveMoney) {
  if (hasLiveMoney) return 'Live Stripe billing.';
  if (mode === 'live' && stripeError) return 'Live billing could not be read. No live revenue yet.';
  if (mode === 'live') return 'Live Stripe billing.';
  if (mode === 'test' && source === 'stripe') return 'Stripe is in test mode. Test charges are not revenue.';
  if (mode === 'test' && stripeError) return 'Stripe test mode could not be read. Plan prices are not revenue.';
  if (mode === 'test') return 'Stripe test mode returned no active subscriptions. Plan prices are not revenue.';
  return 'No live Stripe key. Plan prices are not revenue.';
}

function buildPulse(input, now = new Date()) {
  const source = input || {};
  const profiles = source.profiles || [];
  const teams = source.teams || [];
  const accounts = source.accounts || [];
  const clients = source.clients || [];
  const at = now instanceof Date ? now : new Date(now);
  const book = source.priceBook || {};
  const stripeSubs = source.stripeSubscriptions;
  const mode = source.stripeMode || 'off';
  const liveMode = mode === 'live';
  const stripeReady = Array.isArray(stripeSubs);
  const useStripe = liveMode ? stripeReady : (stripeReady && stripeSubs.length > 0);
  const computed = useStripe
    ? mrrFromStripe(stripeSubs, book)
    : (liveMode ? mrrFromStripe([], book) : mrrFromPlans(profiles, teams, accounts, at));
  const lines = computed.lines;
  const cents = sumLines(lines);
  const trials = trialStats(profiles, at);
  if (useStripe) trials.active = Math.max(trials.active, Number(computed.trialing) || 0);
  const churn = churnStats(
    source.subscriptions || [],
    accounts,
    source.billingEvents || [],
    liveMode && !useStripe ? 0 : computed.payingAgents,
    at,
    useStripe ? computed.cancelling : null,
  );
  const stripeSource = useStripe ? 'stripe' : (liveMode ? 'live' : 'fallback');
  const hasLiveMoney = liveMode && useStripe && cents > 0;
  const customerCount = lines.solo.customers + lines.team.customers + lines.homeowner.customers;
  const listed = lineList(lines);
  const signups = signupCounts(profiles, accounts, at);
  const questions = source.aiQuestionCount != null
    ? source.aiQuestionCount
    : countAiRows(source.aiRows, at);
  const sandbox = liveMode || cents <= 0 ? null : {
    cents,
    arrCents: cents * 12,
    lines: listed,
    payingAgents: computed.payingAgents,
    label: 'Test payments (sandbox, not real revenue)',
  };
  return {
    generatedAt: at.toISOString(),
    stripe: {
      mode,
      source: stripeSource,
      badge: mode === 'test' ? 'Test mode' : null,
      note: stripeNote(mode, stripeSource, source.stripeError === true, hasLiveMoney),
    },
    mrr: {
      cents: hasLiveMoney ? cents : 0,
      arrCents: hasLiveMoney ? cents * 12 : 0,
      customers: liveMode && useStripe ? customerCount : 0,
      payingAgents: liveMode && !useStripe ? 0 : computed.payingAgents,
      teamCustomers: liveMode && !useStripe ? 0 : computed.teamCustomers,
      seats: liveMode && !useStripe ? 0 : lines.team.seats,
      lines: hasLiveMoney ? listed : [],
      live: hasLiveMoney,
      emptyLabel: hasLiveMoney ? '' : 'No live revenue yet',
    },
    sandbox,
    trials,
    churn,
    signups,
    homeowners: countHomeowners(clients, at),
    teams: teamSeatTotals(teams),
    agents: agentRoster(profiles),
    ai: aiUsage(questions, clients, source.aiPerHomeowner),
    charts: {
      mrr: hasLiveMoney ? mrrTrend(source.metricsDaily || [], cents, at) : [],
      mrrLive: hasLiveMoney,
      signups: weeklySignups(profiles, accounts, at),
    },
    lists: {
      recentSignups: recentSignups(profiles, accounts, clients),
      trialsEnding: trialsEnding(profiles, at),
      failedPayments: failedPayments(source.payments || [], profiles, mode !== 'live'),
      inquiries: inquiryRows(source.inquiries || []),
      errors: errorRows(source.clientErrors || [], source.jobs || []),
    },
    activity: buildActivity(source),
  };
}

function countAiRows(rows, now) {
  const start = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1);
  return (rows || []).filter((row) => {
    if (!row || row.client_id == null) return false;
    if (row.conversation_type === 'fresh_start') return false;
    return inWindow(row.created_at, start, null);
  }).length;
}

function sealPulse(value) {
  if (Array.isArray(value)) return value.map(sealPulse);
  if (!value || typeof value !== 'object') return value;
  const out = {};
  Object.keys(value).forEach((key) => {
    if (FORBIDDEN_KEYS.has(key)) return;
    out[key] = sealPulse(value[key]);
  });
  return out;
}

function csvCell(value) {
  const text = value == null ? '' : String(value);
  const guarded = /^[=+\-@]/.test(text) ? `'${text}` : text;
  return `"${guarded.replace(/"/g, '""')}"`;
}

function activityCsv(rows) {
  const header = ['when', 'kind', 'title', 'detail'];
  const lines = [header.join(',')];
  (rows || []).forEach((row) => {
    lines.push([row.at, row.kind, row.title, row.detail].map(csvCell).join(','));
  });
  return lines.join('\n');
}

function snapshotRow(pulse, now = new Date()) {
  const at = now instanceof Date ? now : new Date(now);
  const lines = {};
  ((pulse && pulse.mrr && pulse.mrr.lines) || []).forEach((line) => {
    lines[line.id] = line;
  });
  const cents = (id) => Number((lines[id] && lines[id].cents) || 0);
  return {
    day: at.toISOString().slice(0, 10),
    mrr_cents: Number(pulse && pulse.mrr && pulse.mrr.cents) || 0,
    arr_cents: Number(pulse && pulse.mrr && pulse.mrr.arrCents) || 0,
    mrr_solo_cents: cents('solo'),
    mrr_team_cents: cents('team'),
    mrr_addon_plus_cents: cents('addon_plus'),
    mrr_addon_max_cents: cents('addon_max'),
    mrr_homeowner_cents: cents('homeowner'),
    paying_agents: Number(pulse && pulse.mrr && pulse.mrr.payingAgents) || 0,
    paying_seats: Number(pulse && pulse.mrr && pulse.mrr.seats) || 0,
    team_customers: Number(pulse && pulse.mrr && pulse.mrr.teamCustomers) || 0,
    active_trials: Number(pulse && pulse.trials && pulse.trials.active) || 0,
    signups_agents: Number(pulse && pulse.signups && pulse.signups.today && pulse.signups.today.agents) || 0,
    signups_homeowners: Number(pulse && pulse.signups && pulse.signups.today && pulse.signups.today.homeowners) || 0,
    ai_questions: Number(pulse && pulse.ai && pulse.ai.questions) || 0,
    source: 'snapshot',
  };
}

export {
  AGENT_ROLES,
  AI_PER_HOMEOWNER,
  SOLO_CENTS,
  TEAM_CENTS,
  PLUS_CENTS,
  MAX_CENTS,
  FORBIDDEN_KEYS,
  adminMfaRequired,
  adminPageRequiresAal2,
  pulseStripeKey,
  tokenAal,
  isPlatformAgent,
  buildPulse,
  sealPulse,
  activityCsv,
  snapshotRow,
  mrrFromStripe,
  mrrFromPlans,
  money,
};
